import type { AdapterHealth, SystemStatus } from "../hooks/useSystemHealth";

/**
 * When the red "SYSTEM DEGRADED" bar across the top of the Mayor's screen is
 * worth showing.
 *
 * It used to render whenever the rollup was anything but "healthy", and said
 * only "1 adapter down". Adapter health lives in each Worker isolate's memory,
 * so one slow upstream on one isolate was enough to put a permanent-looking
 * red alarm above a dashboard that was fine — naming no adapter and offering
 * no action. An alarm that fires for things nobody acts on gets ignored, which
 * costs us the one time it matters.
 *
 * Policy: stay quiet for a single non-critical wobble, shout when the water
 * mission is affected or when several feeds fail at once — and always name
 * what broke.
 */

/**
 * Feeds the dashboard exists for. Lam Takhong IS the town's water supply, and
 * drought/rain drive the decisions this screen is briefing. Losing one of
 * these is worth interrupting the Mayor for; losing Google Trends is not.
 */
export const CRITICAL_ADAPTERS: readonly string[] = [
  "lam-takhong",
  "basin-water",
  "drought",
  "weather",
  "precip-nowcast",
];

/** Failing at once before we call it systemic, even if none are critical. */
const NOISE_FLOOR = 2;

export interface BannerVerdict {
  severity: "down" | "degraded";
  /** Adapter names to name in the banner, critical ones first. */
  names: string[];
  critical: boolean;
}

function isFailing(status: SystemStatus): boolean {
  // "unavailable" is a permanent, known state — an unconfigured optional key
  // (Facebook) or an upstream with no coverage here. It is a configuration
  // fact, not an incident, and must never raise the banner.
  return status === "down" || status === "degraded";
}

/**
 * @returns null when the banner should stay hidden.
 */
export function systemBannerVerdict(
  system: { status: SystemStatus; down: number; degraded: number },
  adapters: readonly AdapterHealth[],
): BannerVerdict | null {
  const failing = adapters.filter((a) => isFailing(a.status));
  if (failing.length === 0) {
    // Trust the adapter list over the rollup: a rollup that says "degraded"
    // with nothing actually failing produced the empty "SYSTEM DEGRADED — ·"
    // banner people learned to ignore.
    return null;
  }

  const criticalFailures = failing.filter((a) => CRITICAL_ADAPTERS.includes(a.name));
  const systemic = system.status === "down" || failing.length >= NOISE_FLOOR;
  if (criticalFailures.length === 0 && !systemic) return null;

  const names = [
    ...criticalFailures.map((a) => a.name),
    ...failing.filter((a) => !CRITICAL_ADAPTERS.includes(a.name)).map((a) => a.name),
  ];

  return {
    severity: failing.some((a) => a.status === "down") ? "down" : "degraded",
    names,
    critical: criticalFailures.length > 0,
  };
}

/** Banner copy: always names what broke, so it can be acted on. */
export function bannerMessage(v: BannerVerdict): string {
  const shown = v.names.slice(0, 3).join(", ");
  const rest = v.names.length > 3 ? ` +${v.names.length - 3}` : "";
  const what = v.critical ? "WATER FEED" : "FEED";
  const verb = v.severity === "down" ? "DOWN" : "DEGRADED";
  return `${what} ${verb} — ${shown}${rest}`;
}
